/*
 * Flavor / multi-choice selector
 * -----------------------------------------------------------------------------
 * Drives the "flavor_selector" product block. Lives in an asset (not inline in
 * the snippet) so it also runs for markup injected at runtime — e.g. the Quick
 * view modal, which builds its content with innerHTML and therefore never
 * executes inline <script> tags.
 *
 * All configuration is read from data-* attributes on the block root, so the
 * same code serves every instance on the page.
 */
(function () {
  var W = (window.JhangoFlavor = window.JhangoFlavor || { blocks: [], bound: false });

  var ATC_SELECTOR = '.btn-atc, .btn-buy, .shopify-payment-button, .product-dynamic-buttons';
  var SCOPE_SELECTOR = '.modal, .offcanvas';

  /* ---------------------------------------------------------------- scoping */

  // Drop blocks whose DOM was torn down (e.g. quick view re-rendered).
  function live() {
    W.blocks = W.blocks.filter(function (b) { return b.el.isConnected; });
    return W.blocks;
  }

  // Which blocks govern this element? Keeps a quick-view product independent
  // from the product on the page behind it.
  function blocksFor(el) {
    if (!el || !el.closest) return [];
    var all = live();

    // The sticky ATC buys the page's main product, but lives in its own form,
    // so map it to the page's (non-modal) selectors.
    if (el.closest('#sticky-atc')) {
      return all.filter(function (b) { return !b.el.closest(SCOPE_SELECTOR); });
    }

    // Buttons inside the same form as a selector: that product's own Add to
    // cart / Buy now / dynamic checkout (product page, or Quick view).
    var form = el.closest('form');
    if (form) {
      var inForm = all.filter(function (b) { return form.contains(b.el); });
      if (inForm.length) return inForm;
    }

    // Anything else is a DIFFERENT product — product cards, cross-sells,
    // recommended products, cart upsells, add-ons — and must never be gated.
    return [];
  }

  // Only blocks with "Block Add to cart when out of range" enabled can gate.
  function invalidFor(el) {
    return blocksFor(el).filter(function (b) { return b.enforces && !b.isValid(); });
  }

  /* ----------------------------------------------------------- page-wide guard */

  function bindGlobal() {
    if (W.bound) return;
    W.bound = true;

    // Block add-to-cart form submits while the selection is out of range.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.querySelector) return;
      if (!form.querySelector('[name="add"], .btn-atc')) return;
      var bad = invalidFor(form);
      if (bad.length) {
        e.preventDefault();
        e.stopImmediatePropagation();
        bad[0].reveal();
      }
    }, true);

    // Block buy-now / express checkout / ATC clicks and reveal the selector.
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest(ATC_SELECTOR);
      if (!t) return;
      var bad = invalidFor(t);
      if (!bad.length) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      bad[0].reveal();
    }, true);

    W.refresh = function () {
      document.querySelectorAll(ATC_SELECTOR).forEach(function (el) {
        var invalid = invalidFor(el).length > 0;
        el.classList.toggle('flavor-atc-blocked', invalid);
        if (invalid) { el.setAttribute('aria-disabled', 'true'); }
        else { el.removeAttribute('aria-disabled'); }
      });
    };
  }

  /* ------------------------------------------------------------ block set-up */

  function initRoot(root) {
    if (!root || root.dataset.flavorReady === '1') return;
    root.dataset.flavorReady = '1';
    bindGlobal();

    var mode = root.dataset.mode || 'check';
    var baseMin = parseInt(root.dataset.min || '0', 10) || 0;
    var baseMax = parseInt(root.dataset.max || '0', 10) || 0; // 0 = unlimited
    var scaleMin = root.dataset.scaleMin === 'true';
    var scaleMax = root.dataset.scaleMax === 'true';
    var outputMode = root.dataset.outputMode || 'combined';
    var sepToken = root.dataset.separator || 'comma';
    var propName = root.dataset.propertyName || 'Selected';
    var valuePrefix = root.dataset.valuePrefix || '';
    var valueSuffix = root.dataset.valueSuffix || '';
    var selectedText = root.dataset.selectedText || '✓';
    var qtyFormat = root.dataset.qtyFormat || ' x{qty}';
    var enforceMax = root.dataset.enforceMax === 'true';
    var blockAtc = root.dataset.blockAtc === 'true';
    var doScroll = root.dataset.scroll !== 'false';

    var propsHost = root.querySelector('[data-flavor-props]');
    var counterEl = root.querySelector('[data-flavor-counter]');
    var messageEl = root.querySelector('[data-flavor-message]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-flavor-item]'));
    var mainForm = root.closest('form');
    var inScope = !!root.closest(SCOPE_SELECTOR); // inside quick view / drawer

    var currentValid = true;
    var observer = null;
    var watchTarget = mainForm || root.closest('.product-main') || root.parentNode;

    function separator() {
      switch (sepToken) {
        case 'pipe': return ' | ';
        case 'newline': return '\r\n';
        // Comma + CRLF, byte-identical to the live theme's POS payload:
        // "Flavor (Qty: 1), \r\nFlavor (Qty: 1)". Lets a POS/KOT that splits
        // the value on line breaks put each flavor on its own line.
        case 'comma_newline': return ', \r\n';
        case 'dash': return ' - ';
        case 'bullet': return ' • ';
        default: return ', ';
      }
    }

    // Effective limits, optionally scaled by the product quantity field.
    function limits() {
      var q = 1;
      if (mainForm) {
        var qi = mainForm.querySelector('input[name="quantity"]');
        if (qi) { q = parseInt(qi.value, 10) || 1; if (q < 1) q = 1; }
      }
      return {
        q: q,
        min: baseMin * (scaleMin ? q : 1),
        max: baseMax > 0 ? baseMax * (scaleMax ? q : 1) : 0
      };
    }

    function itemQty(el) {
      if (mode === 'qty') return parseInt(el.dataset.qty || '0', 10) || 0;
      var cb = el.querySelector('input[data-flavor-option]');
      return cb && cb.checked ? 1 : 0;
    }

    function selected() {
      var out = [];
      items.forEach(function (el) {
        var qty = itemQty(el);
        if (qty > 0) out.push({ label: el.dataset.label || '', qty: qty });
      });
      return out;
    }

    function totalCount() {
      var n = 0;
      items.forEach(function (el) { n += itemQty(el); });
      return n;
    }

    function fmtVal(label, qty) {
      var v = valuePrefix + label + valueSuffix;
      // In quantity mode always append the count (the live theme shows "(Qty: 1)"
      // too), so the cart / POS value matches the live theme byte-for-byte.
      if (mode === 'qty') v += qtyFormat.replace(/\{qty\}/g, qty);
      return v;
    }

    // Update the STATIC property inputs rendered by Liquid (do not create new
    // ones). This theme's add-to-cart reads the property fields present when the
    // form initializes, so injecting inputs later is not picked up. An input with
    // no value is disabled so it isn't submitted as a blank line-item property.
    function setProp(input, value) {
      if (!input) return;
      if (value === '' || value == null) { input.value = ''; input.disabled = true; }
      else { input.value = value; input.disabled = false; }
    }

    function buildProps(list) {
      if (outputMode === 'numbered') {
        var slots = propsHost.querySelectorAll('[data-prop-slot]');
        Array.prototype.forEach.call(slots, function (inp, i) {
          if (i < list.length) setProp(inp, fmtVal(list[i].label, list[i].qty));
          else setProp(inp, '');
        });
      } else if (outputMode === 'separate') {
        var sel = {};
        list.forEach(function (s) { sel[s.label] = s.qty; });
        var inputs = propsHost.querySelectorAll('[data-prop-label]');
        Array.prototype.forEach.call(inputs, function (inp) {
          var label = inp.getAttribute('data-prop-label');
          if (Object.prototype.hasOwnProperty.call(sel, label)) {
            setProp(inp, mode === 'qty' ? String(sel[label]) : selectedText);
          } else setProp(inp, '');
        });
      } else {
        var combined = propsHost.querySelector('[data-prop-combined]');
        setProp(combined, list.length ? list.map(function (s) { return fmtVal(s.label, s.qty); }).join(separator()) : '');
      }
    }

    function fill(tpl, count, lim) {
      return (tpl || '')
        .replace(/\{count\}/g, count)
        .replace(/\{min\}/g, lim.min)
        .replace(/\{max\}/g, lim.max > 0 ? lim.max : '∞')
        .replace(/\{qty\}/g, lim.q);
    }

    function isValid(count, lim) {
      if (count < lim.min) return false;
      if (lim.max > 0 && count > lim.max) return false;
      return true;
    }

    // Mirror properties into the sticky-ATC form (main page instances only).
    function syncSticky() {
      if (inScope) return;
      var stickyForm = document.querySelector('#sticky-atc form');
      if (!stickyForm || stickyForm === mainForm) return;
      Array.prototype.slice.call(stickyForm.querySelectorAll('[data-flavor-synced]'))
        .forEach(function (n) { n.remove(); });
      Array.prototype.slice.call(propsHost.children).forEach(function (src) {
        var clone = src.cloneNode(true);
        clone.removeAttribute('data-flavor-prop');
        clone.setAttribute('data-flavor-synced', '');
        stickyForm.appendChild(clone);
      });
    }

    function apply() {
      if (observer) observer.disconnect();

      var lim = limits();
      var list = selected();
      var count = totalCount();

      items.forEach(function (el) {
        el.classList.toggle('is-selected', itemQty(el) > 0);
      });

      var atMax = enforceMax && lim.max > 0 && count >= lim.max;
      if (mode === 'qty') {
        items.forEach(function (el) {
          var plus = el.querySelector('[data-qty-plus]');
          var minus = el.querySelector('[data-qty-minus]');
          if (plus) plus.disabled = atMax;
          if (minus) minus.disabled = itemQty(el) <= 0;
        });
      } else if (enforceMax && lim.max > 0) {
        items.forEach(function (el) {
          var cb = el.querySelector('input[data-flavor-option]');
          var lock = atMax && cb && !cb.checked;
          if (cb) cb.disabled = lock;
          el.classList.toggle('is-disabled', !!lock);
        });
      }

      buildProps(list);

      currentValid = isValid(count, lim);
      root.classList.toggle('is-valid', currentValid);

      if (counterEl) counterEl.textContent = fill(root.dataset.counterTemplate, count, lim);
      if (messageEl) {
        var msg = '';
        if (count < lim.min) msg = fill(root.dataset.minMessage, count, lim);
        else if (lim.max > 0 && count > lim.max) msg = fill(root.dataset.maxMessage, count, lim);
        messageEl.textContent = msg;
        messageEl.hidden = msg === '';
      }

      syncSticky();
      if (W.refresh) W.refresh();

      if (observer && watchTarget) observer.observe(watchTarget, { childList: true, subtree: true });
    }

    /* interaction */
    if (mode === 'qty') {
      items.forEach(function (el) {
        var plus = el.querySelector('[data-qty-plus]');
        var minus = el.querySelector('[data-qty-minus]');
        var valEl = el.querySelector('[data-qty-value]');
        function set(n) {
          if (n < 0) n = 0;
          el.dataset.qty = n;
          if (valEl) valEl.textContent = n;
        }
        if (plus) plus.addEventListener('click', function () {
          var lim = limits();
          if (lim.max > 0 && totalCount() >= lim.max) { apply(); return; }
          set(itemQty(el) + 1);
          apply();
        });
        if (minus) minus.addEventListener('click', function () { set(itemQty(el) - 1); apply(); });
      });
    } else {
      items.forEach(function (el) {
        var cb = el.querySelector('input[data-flavor-option]');
        if (cb) cb.addEventListener('change', apply);
      });
    }

    if (mainForm) {
      mainForm.addEventListener('change', apply);
      mainForm.addEventListener('input', apply);
    }

    // Re-assert after the theme re-renders the ATC on variant change.
    if ('MutationObserver' in window) {
      var raf = null;
      observer = new MutationObserver(function () {
        if (raf) return;
        raf = requestAnimationFrame(function () { raf = null; apply(); });
      });
    }

    W.blocks.push({
      el: root,
      enforces: blockAtc,
      isValid: function () { return currentValid; },
      reveal: function () {
        if (doScroll) {
          try { root.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
          catch (err) { root.scrollIntoView(); }
        }
        root.classList.remove('flavor-flash');
        void root.offsetWidth;
        root.classList.add('flavor-flash');
        var f = root.querySelector('input[data-flavor-option], [data-qty-plus]');
        if (f) { try { f.focus({ preventScroll: true }); } catch (err) {} }
      }
    });

    apply();
  }

  /* --------------------------------------------------------------- discovery */

  function scan() {
    document.querySelectorAll('[data-flavor-selector]').forEach(initRoot);
  }

  function start() {
    scan();

    // Catch blocks injected at runtime (quick view, drawers, section renders).
    if ('MutationObserver' in window) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) return;
        pending = requestAnimationFrame(function () { pending = null; scan(); });
      }).observe(document.body, { childList: true, subtree: true });
    }

    // Belt and braces for Bootstrap modals / offcanvas.
    ['shown.bs.modal', 'shown.bs.offcanvas'].forEach(function (evt) {
      document.addEventListener(evt, scan);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
